
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { productsData } from '../../../mocks/products';
import { usePriceMonitor } from '../../../hooks/usePriceMonitor';
import ShareModal from '../../../components/feature/ShareModal';

const TopDealsSection = () => {
  const { addToTracking, isTracked } = usePriceMonitor();
  const [shareModal, setShareModal] = useState<{ isOpen: boolean; product: typeof topDeals[0] | null }>({
    isOpen: false,
    product: null
  });

  const topDeals = productsData
    .map(product => {
      const lowestRetailer = product.retailers.reduce((min, r) => r.price < min.price ? r : min);
      const savings = product.highestPrice - lowestRetailer.price;
      const discountPercent = Math.round((savings / product.highestPrice) * 100);
      return {
        ...product,
        currentPrice: lowestRetailer.price,
        lowestRetailer,
        savings,
        discountPercent,
      };
    })
    .sort((a, b) => b.discountPercent - a.discountPercent)
    .slice(0, 6);

  const handleTrack = (product: typeof topDeals[0]) => {
    addToTracking({
      id: product.id,
      name: product.name,
      image: product.image,
      currentPrice: product.currentPrice,
      retailer: product.lowestRetailer.name,
    });
  };

  const handleShare = (product: typeof topDeals[0]) => {
    setShareModal({ isOpen: true, product });
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-fire-fill text-orange-500 text-xl"></i>
              <span className="text-sm font-semibold text-orange-500 uppercase tracking-wider">Hot Deals</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Today&apos;s Best Discounts
            </h2>
          </div>
          <Link
            to="/deals"
            className="hidden md:flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700 transition-colors cursor-pointer"
          >
            View All Deals
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topDeals.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 group"
            >
              {/* Image */}
              <Link to={`/product/${product.id}`} className="block cursor-pointer">
                <div className="relative aspect-[4/3] bg-gray-50 p-6">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Discount Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold rounded-full">
                    -{product.discountPercent}%
                  </div>
                  {/* Savings Badge */}
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-full">
                    Save ${product.savings}
                  </div>
                </div>
              </Link>

              {/* Content */}
              <div className="p-5">
                <p className="text-xs text-gray-500 mb-1">{product.category}</p>
                <Link to={`/product/${product.id}`} className="cursor-pointer">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 hover:text-teal-600 transition-colors line-clamp-1">
                    {product.name}
                  </h3>
                </Link>

                {/* Price */}
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-2xl font-bold text-gray-900">${product.currentPrice}</span>
                  <span className="text-sm text-gray-400 line-through">${product.highestPrice}</span>
                </div>

                <p className="text-xs text-gray-500 mb-4">
                  Best price at <span className="font-medium text-gray-700">{product.lowestRetailer.name}</span>
                </p>

                {/* Actions */}
                <div className="flex gap-2">
                  <Link
                    to={`/product/${product.id}`}
                    className="flex-1 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                  >
                    View Deal
                  </Link>
                  <button
                    onClick={() => handleTrack(product)}
                    disabled={isTracked(product.id)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                      isTracked(product.id)
                        ? 'bg-teal-100 text-teal-600'
                        : 'border border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                    title={isTracked(product.id) ? 'Tracking' : 'Track Price'}
                  >
                    <i className={isTracked(product.id) ? 'ri-check-line' : 'ri-line-chart-line'}></i>
                  </button>
                  <button
                    onClick={() => handleShare(product)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all cursor-pointer"
                    title="Share Deal"
                  >
                    <i className="ri-share-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile View All */}
        <div className="mt-8 text-center md:hidden">
          <Link
            to="/deals"
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Deals
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      {/* Share Modal */}
      {shareModal.product && (
        <ShareModal
          isOpen={shareModal.isOpen}
          onClose={() => setShareModal({ isOpen: false, product: null })}
          title={`${shareModal.product.name} - ${shareModal.product.discountPercent}% OFF!`}
          url={`${window.location.origin}/product/${shareModal.product.id}`}
          description={`Save $${shareModal.product.savings} on ${shareModal.product.name} at ${shareModal.product.lowestRetailer.name}!`}
          image={shareModal.product.image}
        />
      )}
    </section>
  );
};

export default TopDealsSection;
